/**
 * NiV Clinic Detail — constants, labels and formatters shared by the tabs.
 */

import { Wallet, Buildings, Robot } from "@phosphor-icons/react";
import { UNLIMITED } from "../../../../services/planLimits";

// ============================================
// TABS
// ============================================

export const TABS = [
  { id: "details", label: "פרטי הקליניקה", icon: Buildings },
  { id: "manage", label: "ניהול", icon: Wallet },
  { id: "lenny", label: "לני", icon: Robot },
];
export const TAB_IDS = TABS.map((t) => t.id);

// Deep links (MorningBriefing / LennyReviewQueue) pass location.state.tab.
// Every pre-Oct-2026 info tab is folded into "details".
export const LEGACY_TAB_MAP = {
  usage: "details",
  overview: "details",
  telemetry: "details",
  "clinic-info": "details",
};

export const resolveInitialTab = (stateTab) => {
  const mapped = LEGACY_TAB_MAP[stateTab] ?? stateTab;
  return TAB_IDS.includes(mapped) ? mapped : "details";
};

// Details tab sections (order = page order; the sticky jump bar reads this)
export const DETAIL_SECTIONS = [
  { id: "flags", label: "דגלים" },
  { id: "kpis", label: "מדדים" },
  { id: "identity", label: "פרטים" },
  { id: "activity", label: "פעילות" },
  { id: "usage", label: "שימוש" },
  { id: "quotas", label: "מכסות" },
  { id: "messaging", label: "הודעות" },
  { id: "features", label: "פיצ'רים" },
  { id: "finance", label: "הכנסות" },
  { id: "team", label: "צוות" },
  { id: "system", label: "מערכת" },
];

// ============================================
// USAGE LABELS
// ============================================

// AI feature keys (match AI_FUNCTIONS in functions/usageTrackingHelper.js) → Hebrew labels
export const AI_FUNCTION_LABELS = {
  generateTreatmentSummary: "סיכום טיפול",
  enrichTreatmentSummary: "העשרת סיכום טיפול",
  aiTreatmentChat: "צ'אט טיפול",
  aiTreatmentConsultation: "ייעוץ טיפול (סשן חי)",
  aiPatientConsultation: "ייעוץ מטופל (סשן חי)",
  aiGameSelection: "בחירת משחק (סשן חי)",
  generateInitialPatientSummary: "סיכום מטופל ראשוני",
  updatePatientSummary: "עדכון סיכום מטופל",
  rebuildPatientSummary: "בנייה מחדש של סיכום מטופל",
  generateTreatmentsReportSummary: "דוח סיכום טיפולים",
  aiSupportChat: "לני (תמיכה)",
  aiFoodEstimate: "פירוק ארוחה (תזונה)",
};

export const getAiFunctionLabel = (key) => AI_FUNCTION_LABELS[key] || key;

/** Rows sorted by tokens desc; unknown keys included so nothing is hidden */
export const buildAiBreakdownRows = (currentByFn, totalByFn) => {
  const keys = new Set([
    ...Object.keys(AI_FUNCTION_LABELS),
    ...Object.keys(currentByFn || {}),
    ...Object.keys(totalByFn || {}),
  ]);
  return Array.from(keys)
    .map((key) => ({
      key,
      label: getAiFunctionLabel(key),
      monthRequests: currentByFn?.[key]?.requests || 0,
      monthTokens: currentByFn?.[key]?.tokens || 0,
      totalRequests: totalByFn?.[key]?.requests || 0,
      totalTokens: totalByFn?.[key]?.tokens || 0,
    }))
    .filter((r) => r.totalRequests > 0 || r.totalTokens > 0 || r.monthRequests > 0 || r.monthTokens > 0)
    .sort((a, b) => b.totalTokens - a.totalTokens || b.monthTokens - a.monthTokens);
};

// SMS sources (functions/usageTrackingHelper.js SMS_SOURCES) → label + tone class
export const SMS_SOURCES = [
  { key: "appointment", label: "פגישות", tone: "appointment" },
  { key: "reminder", label: "תזכורות", tone: "reminder" },
  { key: "recurringReminder", label: "תזכורות חוזרות", tone: "recurring" },
  { key: "digitalSignature", label: "חתימות", tone: "signature" },
  { key: "questionnaire", label: "שאלונים", tone: "questionnaire" },
  { key: "therapistCancellationAlert", label: "ביטולים", tone: "muted" },
  { key: "bookingRequestAlert", label: "בקשות תור", tone: "muted" },
  { key: "endMeeting", label: "סיום פגישה", tone: "muted" },
  { key: "paymentSummary", label: "סיכום תשלום", tone: "muted" },
];

// ============================================
// PLAN LIMIT ROWS (display registry — order = display order)
// ============================================

/**
 * type: "count" (int, 1100 = unlimited) | "int" (plain int) | "bool" | "readonly"
 * source: "override" (planOverrides map) | "legacy" (flat clinic field) | "plan"
 */
export const LIMIT_ROWS = [
  { key: "smsPerMonth", label: "SMS בחודש", type: "count", source: "override", unit: "" },
  { key: "aiActionsPerMonth", label: "פעולות AI בחודש", type: "count", source: "override" },
  { key: "maxPatients", label: "מטופלים", type: "count", source: "override" },
  { key: "maxTherapists", label: "מטפלים", type: "count", source: "override" },
  { key: "maxRooms", label: "חדרי טיפול", type: "count", source: "override" },
  { key: "canAddTherapists", label: "הוספת מטפלים", type: "bool", source: "override", hint: "כיבוי → מטפל יחיד" },
  { key: "canCreateRooms", label: "יצירת חדרים", type: "bool", source: "override", hint: "כיבוי → 0 חדרים" },
  { key: "canIntegrateMorning", label: "אינטגרציית חשבוניות (מורנינג)", type: "bool", source: "override" },
  { key: "canIntegrateGrow", label: "סליקה דרך Grow (דרישות תשלום)", type: "bool", source: "override" },
  { key: "canUseLeadsWebhook", label: "קליטת מתעניינים אוטומטית (Webhook)", type: "bool", source: "override" },
  { key: "leadsInboundPerMonth", label: "מתעניינים נקלטים בחודש", type: "count", source: "override" },
  { key: "storageMb", label: "אחסון (MB)", type: "int", source: "legacy", field: "allowedStorageInMbMaxPerClinic" },
  { key: "videoMinutesPerMonth", label: "דקות וידאו בחודש", type: "int", source: "legacy", field: "videoMeetingMinutesLimit" },
  { key: "aiTreatmentsReportEnabled", label: "דוח סיכום טיפולים AI", type: "bool", source: "legacy", field: "aiTreatmentsReportEnabled" },
  { key: "aiTreatmentsReportLimit", label: "דוחות AI בחודש", type: "int", source: "legacy", field: "aiTreatmentsReportMonthlyLimit", hint: "בנוסף: 20 ביום למטפל, 20 שניות בין יצירות" },
  { key: "trialDays", label: "ימי ניסיון", type: "readonly", source: "plan", trialOnly: true },
];
export const LIMIT_LABELS = Object.fromEntries(LIMIT_ROWS.map((r) => [r.key, r.label]));

// ============================================
// FORMATTERS
// ============================================

export const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "—";
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "ILS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Jerusalem",
  });
};

export const formatDateTime = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Jerusalem",
  });
};

/** "2026-03" → "מרץ 26" */
export const formatMonthKey = (monthKey) => {
  if (!monthKey) return "";
  const [y, m] = String(monthKey).split("-").map(Number);
  if (!y || !m) return monthKey;
  return new Date(y, m - 1, 1).toLocaleDateString("he-IL", { month: "short", year: "2-digit" });
};

export const getDaysUntilTrialEnd = (trialEndDate) => {
  if (!trialEndDate) return null;
  const endDate = new Date(trialEndDate);
  const now = new Date();
  return Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
};

/** Count limit for display: 1100 sentinel → "∞" */
export const formatLimit = (n) => {
  if (n == null) return "—";
  return Number(n) >= UNLIMITED ? "∞" : Number(n).toLocaleString("he-IL");
};

/** Display value for any limit row */
export const formatLimitValue = (row, value) => {
  if (row.type === "bool") return value ? "כן" : "לא";
  if (row.type === "count") return formatLimit(value);
  if (value == null) return "—";
  return Number(value).toLocaleString("he-IL");
};

/** Israel-timezone YYYY-MM-DD */
export const toIsraelDateKey = (date) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

/** Last N Israel day keys, oldest → newest */
export const lastNDayKeys = (n) => {
  const keys = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    keys.push(toIsraelDateKey(new Date(Date.now() - i * 24 * 60 * 60 * 1000)));
  }
  return keys;
};

/** Plan tone class (CSS decides the colors) */
export const planToneClass = (plan) => {
  if (!plan) return "ncd2-plan-default";
  if (plan === "custom1") return "ncd2-plan-pilot";
  if (plan.startsWith("custom")) return "ncd2-plan-custom";
  return `ncd2-plan-${plan}`;
};

// Money-by-month chart series (fixed order, Finance page status colors).
// Palette validated with the dataviz validator (light surface, all checks pass).
export const MONEY_SERIES = [
  { key: "expected", label: "צפוי", color: "#2563eb" },
  { key: "pending", label: "ממתין", color: "#b45309" },
  { key: "invoiced", label: "חשבונית", color: "#7c3aed" },
  { key: "paid", label: "שולם", color: "#3cc4a1" },
];

/** recharts animations off under prefers-reduced-motion */
export const chartAnimationsEnabled = () =>
  !(typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

export const buildWhatsappLink = (phone) =>
  `https://wa.me/${String(phone || "").replace(/\D/g, "").replace(/^0/, "972")}`;
